<template>
  <div class="surplus-manager-dashboard">
    <!-- Header multi-cantines : périmètre = cantines où l'appelant est gestionnaire de surplus -->
    <dashboard-header title="GESTION SURPLUS" job="SURPLUSMANAGER" @scope-change="onScopeChange" />

    <!-- Cartes principales -->
    <div class="cards-grid">
      <!-- Card 1: Nouvelles offres -->
      <dashboard-card
        title="🔔 Nouvelles offres"
        description="Surplus disponibles sur vos cantines"
        :badge="String(surplusOffers.length)"
        badge-variant="orange"
        link-label="Voir les offres"
        variant="featured"
        @click="showAllOffers = true"
      />

      <!-- Card 2: Mes réservations -->
      <dashboard-card
        title="📋 Mes réservations"
        description="Collectes prévues et historique"
        :badge="`${myReservations.length} en cours`"
        link-label="Consulter"
        @click="showReservations = true"
      />

      <!-- Card 3: Statistiques -->
      <dashboard-card
        title="📊 Mes statistiques"
        description="Impact et denrées valorisées"
        badge="Ce mois"
        badge-variant="green"
        link-label="Voir"
        @click="showStats = true"
      />

      <!-- Card 4: Historique -->
      <dashboard-card
        title="📅 Historique"
        description="Toutes mes collectes passées"
        link-label="Consulter"
        @click="showHistory = true"
      />
    </div>

    <!-- Modal: Toutes les offres -->
    <div v-if="showAllOffers" class="modal-overlay" @click="showAllOffers = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showAllOffers = false">✕</button>
        <h2 class="modal-title">🔔 Nouvelles offres ({{ surplusOffers.length }})</h2>
        <p class="modal-subtitle">Surplus disponibles sur votre périmètre</p>

        <div v-if="offersLoading" style="text-align:center;color:#6b7280;padding:1rem">Chargement…</div>
        <div v-else class="offers-list">
          <div v-for="offer in surplusOffers" :key="offer.id" class="offer-card" @click="viewOfferDetails(offer)">
            <div class="offer-header">
              <h4 class="offer-school">🏫 {{ offer.canteenName || offer.canteen_name || "—" }}</h4>
            </div>
            <div class="offer-details">
              <div class="offer-quantity">
                📦 {{ offer.items && offer.items.quantity_kg ? offer.items.quantity_kg + " kg" : "—" }}
              </div>
              <div class="offer-items">{{ offer.items && offer.items.dish_name ? offer.items.dish_name : "—" }}</div>
            </div>
            <div class="offer-footer">
              <span class="offer-time">📅 {{ offer.mealDate || offer.meal_date }}</span>
              <button @click.stop="reserveOffer(offer)" :disabled="reservingId === offer.id" class="btn-reserve">
                {{ reservingId === offer.id ? "…" : "✅ Réserver" }}
              </button>
              <button
                @click.stop="cancelOffer(offer)"
                :disabled="cancellingOfferId === offer.id"
                class="btn-cancel-offer"
              >
                {{ cancellingOfferId === offer.id ? "…" : "✕ Annuler l'offre" }}
              </button>
            </div>
          </div>
          <p v-if="surplusOffers.length === 0" style="text-align:center;color:#6b7280">Aucune offre disponible.</p>
        </div>
      </div>
    </div>

    <!-- Modal: Détail d'une offre -->
    <div v-if="selectedOffer" class="modal-overlay" @click="selectedOffer = null">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="selectedOffer = null">✕</button>
        <h2 class="modal-title">{{ selectedOffer.canteenName || selectedOffer.canteen_name }}</h2>

        <div class="detail-section">
          <h3 class="section-title">Détails du surplus</h3>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">Quantité</span>
              <span class="info-value">
                {{
                  selectedOffer.items && selectedOffer.items.quantity_kg ? selectedOffer.items.quantity_kg + " kg" : "—"
                }}
              </span>
            </div>
            <div class="info-item">
              <span class="info-label">Denrée</span>
              <span class="info-value">
                {{ selectedOffer.items && selectedOffer.items.dish_name ? selectedOffer.items.dish_name : "—" }}
              </span>
            </div>
            <div class="info-item">
              <span class="info-label">Date</span>
              <span class="info-value">{{ selectedOffer.mealDate || selectedOffer.meal_date }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">État</span>
              <span class="info-value">
                {{ selectedOffer.items && selectedOffer.items.state ? selectedOffer.items.state : "—" }}
              </span>
            </div>
          </div>
        </div>

        <button
          @click="reserveOffer(selectedOffer)"
          :disabled="reservingId === selectedOffer.id"
          class="btn btn-primary"
        >
          {{ reservingId === selectedOffer.id ? "Réservation en cours…" : "✅ Réserver cette offre" }}
        </button>
        <button
          @click="cancelOffer(selectedOffer)"
          :disabled="cancellingOfferId === selectedOffer.id"
          class="btn btn-danger"
        >
          {{ cancellingOfferId === selectedOffer.id ? "Annulation…" : "✕ Annuler cette offre" }}
        </button>
        <button @click="selectedOffer = null" class="btn btn-secondary">Fermer</button>
      </div>
    </div>

    <!-- Modal: Réservations -->
    <div v-if="showReservations" class="modal-overlay" @click="showReservations = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showReservations = false">✕</button>
        <h2 class="modal-title">📋 Mes réservations</h2>

        <div v-if="reservationsLoading" style="text-align:center;color:#6b7280;padding:1rem">Chargement…</div>
        <div v-else>
          <div
            v-for="res in myReservations"
            :key="res.id"
            style="background:#f9fafb;padding:1rem;border-radius:8px;margin-bottom:0.75rem"
          >
            <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:0.5rem">
              <strong style="font-size:0.875rem">
                {{ res.offerDetail && res.offerDetail.canteenName ? res.offerDetail.canteenName : "—" }}
              </strong>
              <span
                class="badge"
                :class="
                  res.status === 'CONFIRMED' ? 'badge-green' : res.status === 'CANCELLED' ? 'badge-red' : 'badge-blue'
                "
              >
                {{ res.status === "PENDING" ? "En attente" : res.status === "CONFIRMED" ? "Confirmé" : "Annulé" }}
              </span>
            </div>
            <p style="font-size:0.875rem;color:#6b7280;margin:0 0 0.5rem 0">
              {{
                res.offerDetail && res.offerDetail.items
                  ? res.offerDetail.items.dish_name + " — " + res.offerDetail.items.quantity_kg + "kg"
                  : "—"
              }}
            </p>
            <button
              v-if="res.status !== 'CANCELLED'"
              @click="cancelReservation(res)"
              style="font-size:0.75rem;color:#dc2626;background:none;border:none;cursor:pointer;padding:0"
            >
              Annuler
            </button>
          </div>
          <p v-if="myReservations.length === 0" style="text-align:center;color:#6b7280">Aucune réservation.</p>
        </div>
      </div>
    </div>

    <!-- Modal: Statistiques -->
    <div v-if="showStats" class="modal-overlay" @click="showStats = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showStats = false" aria-label="Fermer">✕</button>
        <h2 class="modal-title">📊 Mes statistiques</h2>
        <p class="modal-subtitle">Denrées valorisées sur votre périmètre — seules les collectes confirmées comptent.</p>

        <!-- Période réglable (§5.3) -->
        <div class="period-picker">
          <label class="period-field">
            <span>Du</span>
            <input type="date" v-model="impactFrom" @change="loadImpact" />
          </label>
          <label class="period-field">
            <span>Au</span>
            <input type="date" v-model="impactTo" @change="loadImpact" />
          </label>
        </div>

        <div v-if="impactLoading" style="text-align:center;color:#6b7280;padding:1rem">Chargement…</div>
        <div v-else-if="impact">
          <div class="stats-grid">
            <div class="stat-card stat-green">
              <div class="stat-icon">♻️</div>
              <div class="stat-value">{{ impact.collectedKg }} kg</div>
              <div class="stat-label">Valorisés (collectés)</div>
            </div>
            <div class="stat-card stat-blue">
              <div class="stat-icon">📦</div>
              <div class="stat-value">{{ impact.collectedCount }}</div>
              <div class="stat-label">Collectes confirmées</div>
            </div>
            <div class="stat-card stat-orange">
              <div class="stat-icon">🍽️</div>
              <div class="stat-value">{{ impact.offeredKg }} kg</div>
              <div class="stat-label">Proposés</div>
            </div>
            <!-- Équivalent-repas (§9.6). Le seul chiffre *dérivé* de l'écran : il est marqué
                 « estimation » et sa source est portée juste dessous, parce que le facteur est un
                 usage sectoriel et non un barème réglementaire. -->
            <div v-if="impact.mealEquivalentBasis" class="stat-card stat-purple">
              <div class="stat-icon">🥗</div>
              <div class="stat-value">≈ {{ impact.collectedMealEquivalents }}</div>
              <div class="stat-label">Repas équivalents (estimation)</div>
            </div>
          </div>
          <!-- La source ne s'affiche jamais séparément du chiffre : elle vient du serveur avec lui. -->
          <p v-if="impact.mealEquivalentBasis" class="stats-note">{{ impact.mealEquivalentBasis }}</p>
          <p v-if="impact.itemsWithoutQuantity" class="stats-note">
            ℹ️ {{ impact.itemsWithoutQuantity }} offre(s) sans quantité en kg — non comptées dans les totaux.
          </p>
        </div>
        <p v-else style="text-align:center;color:#6b7280">Aucune donnée sur la période.</p>
      </div>
    </div>

    <!-- Modal: Historique -->
    <div v-if="showHistory" class="modal-overlay" @click="showHistory = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showHistory = false">✕</button>
        <h2 class="modal-title">📅 Historique des collectes</h2>

        <div class="history-list">
          <div v-for="res in allReservations" :key="res.id" class="history-item">
            <div class="history-date">
              {{ (res.offerDetail && (res.offerDetail.mealDate || res.offerDetail.meal_date)) || "—" }}
            </div>
            <div class="history-details">
              <strong>{{ res.offerDetail && res.offerDetail.canteenName ? res.offerDetail.canteenName : "—" }}</strong>
              <span>{{ res.offerDetail && res.offerDetail.items ? res.offerDetail.items.dish_name : "—" }}</span>
            </div>
            <span class="badge badge-green">{{ res.status }}</span>
          </div>
          <p v-if="allReservations.length === 0" style="text-align:center;color:#6b7280">Aucun historique.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DashboardHeader from "@/components/DashboardHeader.vue"
import DashboardCard from "@/components/DashboardCard.vue"

export default {
  name: "SurplusManagerDashboard",
  components: { DashboardHeader, DashboardCard },
  data() {
    // Période par défaut = mois courant (§5.3). Formatage manuel pour éviter le décalage d'un jour
    // que `toISOString` (UTC) introduit près des bornes de mois.
    const now = new Date()
    const iso = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`
    return {
      // Périmètre restreint choisi dans l'en-tête (vide = toutes mes cantines de surplus).
      canteenIds: [],
      showAllOffers: false,
      showReservations: false,
      showStats: false,
      showHistory: false,
      selectedOffer: null,
      surplusOffers: [],
      myReservations: [],
      allReservations: [],
      offersLoading: false,
      reservationsLoading: false,
      reservingId: null,
      cancellingOfferId: null,
      // Indicateurs d'impact lus côté serveur (§5) : seule la quantité collectée mesure un impact.
      impact: null,
      impactLoading: false,
      impactFrom: iso(new Date(now.getFullYear(), now.getMonth(), 1)),
      impactTo: iso(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
    }
  },
  methods: {
    onScopeChange({ canteenIds }) {
      // Le filtre du client ne peut que restreindre le périmètre lu côté serveur (canteen_roles).
      this.canteenIds = canteenIds || []
      this.loadSurplusOffers()
      this.loadMyReservations()
      this.loadImpact()
    },

    async loadImpact() {
      // §5 : l'impact (offert vs collecté, sur la période) est agrégé au serveur, jamais recalculé
      // ici — le client ne voit pas toutes les collectes du périmètre, seulement ses réservations.
      this.impactLoading = true
      try {
        const params = new URLSearchParams({ from: this.impactFrom, to: this.impactTo })
        if (this.canteenIds.length) {
          params.set("canteens", this.canteenIds.join(","))
        }
        const resp = await fetch(`/api/v1/surplusImpact/?${params.toString()}`, { credentials: "same-origin" })
        if (resp.ok) {
          this.impact = await resp.json()
        }
      } catch {
        // silencieux
      } finally {
        this.impactLoading = false
      }
    },

    async loadSurplusOffers() {
      this.offersLoading = true
      try {
        const params = new URLSearchParams({ is_available: "true" })
        if (this.canteenIds.length) {
          params.set("canteens", this.canteenIds.join(","))
        }
        const resp = await fetch(`/api/v1/surplusOffers/?${params.toString()}`, { credentials: "same-origin" })
        if (resp.ok) {
          this.surplusOffers = await resp.json()
        }
      } catch {
        // silencieux
      } finally {
        this.offersLoading = false
      }
    },

    async loadMyReservations() {
      this.reservationsLoading = true
      try {
        const resp = await fetch("/api/v1/surplusReservations/", { credentials: "same-origin" })
        if (resp.ok) {
          const data = await resp.json()
          this.allReservations = data
          this.myReservations = data.filter((r) => r.status !== "CANCELLED")
        }
      } catch {
        // silencieux
      } finally {
        this.reservationsLoading = false
      }
    },

    getCsrfToken() {
      const match = document.cookie.match(/csrftoken=([^;]+)/)
      return match ? match[1] : ""
    },

    viewOfferDetails(offer) {
      this.selectedOffer = offer
      this.showAllOffers = false
    },

    async reserveOffer(offer) {
      this.reservingId = offer.id
      try {
        const resp = await fetch("/api/v1/surplusReservations/", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", "X-CSRFToken": this.getCsrfToken() },
          body: JSON.stringify({ offer: offer.id }),
        })
        if (resp.ok) {
          this.selectedOffer = null
          this.showAllOffers = false
          await Promise.all([this.loadSurplusOffers(), this.loadMyReservations()])
        } else {
          const err = await resp.json().catch(() => ({}))
          alert("Erreur : " + JSON.stringify(err))
        }
      } catch (e) {
        alert("Erreur réseau : " + e.message)
      } finally {
        this.reservingId = null
      }
    },

    async cancelOffer(offer) {
      if (!confirm(`Annuler l'offre « ${offer.items && offer.items.dish_name ? offer.items.dish_name : "surplus"} » ?`))
        return
      this.cancellingOfferId = offer.id
      try {
        // La cantine d'origine vient de l'offre elle-même : le dashboard n'est plus lié à une cantine unique.
        const resp = await fetch(`/api/v1/canteen/${offer.canteen}/surplusOffers/${offer.id}/`, {
          method: "PATCH",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", "X-CSRFToken": this.getCsrfToken() },
          body: JSON.stringify({ is_available: false }),
        })
        if (resp.ok) {
          this.selectedOffer = null
          await this.loadSurplusOffers()
        } else {
          const err = await resp.json().catch(() => ({}))
          alert("Erreur : " + (err.detail || JSON.stringify(err)))
        }
      } catch (e) {
        alert("Erreur réseau : " + e.message)
      } finally {
        this.cancellingOfferId = null
      }
    },

    async cancelReservation(reservation) {
      try {
        const resp = await fetch(`/api/v1/surplusReservations/${reservation.id}/`, {
          method: "PATCH",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", "X-CSRFToken": this.getCsrfToken() },
          body: JSON.stringify({ status: "CANCELLED" }),
        })
        if (resp.ok) {
          await Promise.all([this.loadSurplusOffers(), this.loadMyReservations()])
        }
      } catch {
        // silencieux
      }
    },
  },
}
</script>

<style scoped lang="scss">
// Socle partagé — la vue ne le recopie plus. Elle en garde besoin en propre pour les
// badges de statut des modales, que `DashboardCard` ne rend pas.
@import "../scss/dashboard-card";
/* Styles de base identiques aux autres dashboards */
.surplus-manager-dashboard {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1.5rem;
  background-color: #f8f9fa;
  min-height: 100vh;
}

.dashboard-header {
  margin-bottom: 2rem;
  text-align: center;
}

.dashboard-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
  letter-spacing: 0.5px;
}

.dashboard-subtitle {
  font-size: 0.95rem;
  color: #6b7280;
  margin: 0;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.25rem;
}

@media (max-width: 768px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  overflow-y: auto;
}

.modal-content {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.modal-large {
  max-width: 700px;
}

.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: #f3f4f6;
  border: none;
  cursor: pointer;
  font-size: 1.25rem;
  color: #6b7280;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}

.modal-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 1.5rem 0;
}

/* Liste d'offres */
.offers-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.offer-card {
  background: #f9fafb;
  border-radius: 12px;
  padding: 1.25rem;
  cursor: pointer;
  transition: all 0.2s;
  border: 2px solid transparent;
}

.offer-card:hover {
  background: white;
  border-color: #22c55e;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.offer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.offer-school {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.offer-distance {
  font-size: 0.875rem;
  color: #6b7280;
}

.offer-details {
  margin-bottom: 0.75rem;
}

.offer-quantity {
  font-size: 1.125rem;
  font-weight: 600;
  color: #22c55e;
  margin-bottom: 0.25rem;
}

.offer-items {
  font-size: 0.875rem;
  color: #6b7280;
}

.offer-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.offer-time {
  font-size: 0.875rem;
  color: #f97316;
  font-weight: 500;
}

.btn-reserve {
  background: #22c55e;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
}

.btn-cancel-offer {
  background: none;
  color: #ef4444;
  border: 1px solid #ef4444;
  padding: 0.4rem 0.75rem;
  border-radius: 8px;
  font-size: 0.8rem;
  cursor: pointer;
}
.btn-cancel-offer:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-danger {
  background: #ef4444;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
}

/* Détails */
.detail-section {
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 1rem 0;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
}

.info-item {
  background: #f9fafb;
  padding: 0.75rem;
  border-radius: 8px;
}

.info-label {
  display: block;
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

.info-value {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #1a1a1a;
}

/* Réservations */
.reservation-card {
  background: #f9fafb;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 0.75rem;
}

.reservation-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.reservation-details {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
}

/* Stats */
.stats-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
}

.stat-item {
  text-align: center;
  background: #f9fafb;
  padding: 1rem;
  border-radius: 8px;
}

.stat-value {
  font-size: 1.875rem;
  font-weight: 700;
  color: #1a1a1a;
}

.stat-label {
  font-size: 0.875rem;
  color: #6b7280;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.period-picker {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.period-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.8rem;
  color: #6b7280;
}

.period-field input {
  padding: 0.4rem 0.6rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #1a1a1a;
}

.stats-note {
  font-size: 0.8rem;
  color: #92400e;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 0.6rem 0.75rem;
  margin: 0;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  border: 2px solid;
}

.stat-card.stat-green {
  border-color: #86efac;
  background: #f0fdf4;
}
.stat-card.stat-blue {
  border-color: #93c5fd;
  background: #eff6ff;
}
.stat-card.stat-orange {
  border-color: #fdba74;
  background: #fff7ed;
}
/* Estimation, et non mesure : une teinte distincte des trois grandeurs mesurées. */
.stat-card.stat-purple {
  border-color: #d8b4fe;
  background: #faf5ff;
}

.stat-icon {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
}

.impact-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 8px;
  margin-bottom: 0.5rem;
}

.impact-label {
  font-size: 0.875rem;
  color: #6b7280;
}

.impact-value {
  font-size: 0.875rem;
  font-weight: 700;
  color: #22c55e;
}

/* Historique */
.history-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.history-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
}

.history-date {
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
  min-width: 80px;
}

.history-details {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.history-details strong {
  font-size: 0.875rem;
  color: #1a1a1a;
}

.history-details span {
  font-size: 0.75rem;
  color: #6b7280;
}

/* Buttons */
.btn {
  width: 100%;
  padding: 0.875rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  border: none;
  cursor: pointer;
  margin-bottom: 0.75rem;
}

.btn-primary {
  background: #22c55e;
  color: white;
}

.btn-secondary {
  background: #f3f4f6;
  color: #1a1a1a;
}

@media (max-width: 640px) {
  .surplus-manager-dashboard {
    padding: 1rem;
  }
  .dashboard-title {
    font-size: 1.5rem;
  }
  .info-grid {
    grid-template-columns: 1fr;
  }
}
</style>
